import { useMemo } from 'react'
import type { ConversationSourceDTO } from '@/shared/api'
import { SourceCard } from './SourceCard'

interface Props {
  sources: ConversationSourceDTO[]
}

interface GroupedCounts {
  caso: number
  referencia: number
  outro: number
}

function groupBy(sources: ConversationSourceDTO[]): GroupedCounts {
  const counts: GroupedCounts = { caso: 0, referencia: 0, outro: 0 }
  for (const s of sources) {
    const kind =
      typeof s.metadata_json?.document_kind === 'string'
        ? s.metadata_json.document_kind
        : null
    if (kind === 'reference_document') counts.referencia += 1
    else if (kind === 'case_document') counts.caso += 1
    else counts.outro += 1
  }
  return counts
}

/**
 * Step 8.2 — exibe a contagem agregada por kind acima da lista de chunks.
 * Mostramos apenas os buckets com pelo menos 1 hit para reduzir ruído.
 */
function CountBreakdown({ counts }: { counts: GroupedCounts }) {
  const parts: string[] = []
  if (counts.caso > 0) parts.push(`Caso: ${counts.caso}`)
  if (counts.referencia > 0) parts.push(`Referências: ${counts.referencia}`)
  if (counts.outro > 0) parts.push(`Outros: ${counts.outro}`)
  if (parts.length === 0) return null
  return (
    <div className="mx-[2px] mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] uppercase tracking-[0.08em] text-text-mute">
      {parts.map((p, i) => (
        <span key={p}>
          {p}
          {i < parts.length - 1 ? <span aria-hidden="true"> · </span> : null}
        </span>
      ))}
    </div>
  )
}

export function SourceList({ sources }: Props) {
  const counts = useMemo(() => groupBy(sources), [sources])
  return (
    <div className="aside-section">
      <div className="aside-title">
        <h4>Fontes recuperadas</h4>
        <span className="count">{sources.length} chunks</span>
      </div>
      <CountBreakdown counts={counts} />
      <div className="sources">
        {sources.length === 0 ? (
          <div className="px-2 py-3 text-[12px] text-text-mute">
            Esta resposta não trouxe fontes do índice.
          </div>
        ) : (
          sources.map((s) => <SourceCard key={s.id} source={s} />)
        )}
      </div>
    </div>
  )
}
